import { useCallback, useEffect } from 'react';
import { useClientSelector, useClientDispatch } from 'client/reducers';
import { setCardHeight } from 'client/actions';
import { COMPONENT_TYPE } from 'common/constants';
import { CARD_ROOT_ID, STORY_ELEMENTS_ID } from 'client/constants/common';

const contentSelector = `[data-bb="${COMPONENT_TYPE.CONTENT}"]`;

function Observer(
	this: any,
	{ callback, isEmbedAutoHeight }: { callback: (height: number) => void; isEmbedAutoHeight?: boolean }
) {
	this.height = null;
	this.rafID = 0;

	const { body } = document;
	const items: (HTMLElement | null)[] = [
		document.getElementById(CARD_ROOT_ID),
		document.querySelector(`#${CARD_ROOT_ID} [data-bb="${COMPONENT_TYPE.FLOAT_ABOVE}"]`),
		document.querySelector(`#${CARD_ROOT_ID} [data-bb="${COMPONENT_TYPE.FLOAT_BELOW}"]`),
		document.querySelector(`#${STORY_ELEMENTS_ID} [data-bb="${COMPONENT_TYPE.FLOAT_ABOVE}"]`),
		document.querySelector(`#${STORY_ELEMENTS_ID} [data-bb="${COMPONENT_TYPE.FLOAT_BELOW}"]`),
	];

	this.observe = () => {
		// Measure contentHeight from parentElement, accounting for Content margins and Outbrain widget (if present)
		const contentHeight = document.querySelector(contentSelector)?.parentElement?.clientHeight ?? 0;
		// by default page height is equal to content element height, but not less than window height
		let height = Math.max(contentHeight, window.innerHeight);

		if (isEmbedAutoHeight) {
			/* When a story is embedded in another page with the embed option "autoHeight: true", then the story page
			   must not have scrolling, to achieve this, the iframe containing this story must have a height equal to
			   the height of the content element that actually represents the height of the page. */
			height = contentHeight;
		}

		if (height !== this.height) {
			this.height = height;
			/* Set body height is required, so that the <SelectionHint> can only occupy this allowed height and
			  not create additional scrolling when going beyond it. Also, for the correct display of the selected area,
			  if the height of the content is greater than the height of the window. */
			body.style.height = `${height}px`;
			body.style.position = `relative`;

			/* There was an issue(iOS only), when FLA,FLB children with "align-self: end" were positioned within
			   visible viewport area, but not withing full card height. To fix this behavior was explicitly assigned
			   height to card component and 100% for FLA,FLB blocks. So in general there is no reason to set
			   inline style over css only solution and this assignment, just fixes some unexpected bugs and makes
			   sure that main containers have correct height. */
			items.forEach(item => {
				if (item) item.style.height = `${height}px`; // eslint-disable-line no-param-reassign
			});

			callback(height);
		}
		this.rafID = window.requestAnimationFrame(this.observe);
	};
}

/**
 * @description React component, which purpose is to observe height of the content element, which represents a height
 * of the page and assign this height to main story/card containers to ensure correct behaviour.
 *
 * Why is it done here, in separated component and not in <AutoHeight/> where the content height originally changes?
 * This was done before the height became automatic, in past it was assigned manually in several places (in fields
 * at inspector, as well as by dragging on the canvas for the corresponding point, see <ContentResize />),
 * I decided to leave this logic here, since it remains possible that manual height control will be returned.
 */
const CardHeightObserver = () => {
	const isEmbedAutoHeight = useClientSelector(state => state.settings.embed.autoHeight);
	const dispatch = useClientDispatch();
	const setCardHeightCallback = useCallback(
		(cardHeight: number) => {
			dispatch(setCardHeight(cardHeight));
		},
		[dispatch]
	);

	useEffect(() => {
		const observer = new Observer({ callback: setCardHeightCallback, isEmbedAutoHeight });
		const { observe } = observer;
		observer.rafID = window.requestAnimationFrame(observe);
		return () => {
			window.cancelAnimationFrame(observer.rafID);
		};
	}, [setCardHeightCallback, isEmbedAutoHeight]);

	return null;
};

export default CardHeightObserver;
